/// <amd-module name="VSS/Platform/Context" />

import { deserializeVssJsonObject } from "Util/Serialization";
import {
    IObjectProperty,
    IObservable,
    IObservableObject,
    IObservableValue,
    Observable,
    ObservableObject,
    ObservableValue
} from "VSS/Core/Observable";
import { PageContext } from "./Contracts/Platform";

let defaultRawPageContext: any;
let defaultPageContext: PageContext | undefined;

/**
 * All services registered with the IVssPageContext.registerService MUST implement
 * IVssService and MAY implement a _serviceStart method which is made when the service
 * is first created. This gives the service the chance to capture and work with the
 * IVssPageContext they are tracked with.
 */
export interface IVssService {
    /**
     * serviceStart is called when a service is retrieved from the IVssPageContext
     * and the service implements IVssService.
     */
    _serviceStart?: (pageContext: IVssPageContext) => void;

    /**
     * serviceEnd is called when the pageContext is being unloaded, this gives services
     * the opportunity to clean up anything they need to.
     */
    _serviceEnd?: (pageContext: IVssPageContext) => void;
}

/**
 * An IVssPersistantService is a service that is not recreated when a new page context
 * is created instead the service is kept around and is "restarted".
 */
export interface IVssPersistantService {
    _serviceRestart: (pageContext: IVssPageContext) => void;
}

/**
 * The VssService is a base class that services can chose to use when implementing
 * their services. It adds some core framework abilities to services like observability.
 * If your service acts like a store and wants to allow for external notifications
 * when state is changed this class offers a built in observable.
 */
export abstract class VssService implements IVssService {
    protected pageContext: IVssPageContext;

    public _serviceStart(pageContext: IVssPageContext): void {
        this.pageContext = pageContext;
    }

    public _serviceEnd(pageContext: IVssPageContext): void { }

    public _serviceRestart(pageContext: IVssPageContext): void {
        this.pageContext = pageContext;
    }
}

/**
 * An IVssObservableService<T> is a standard service that offers an observable as part
 * of the service itself. If the service wants to publish events this is an easy
 * model to follow.
 */
export interface IVssObservableService<T, TAction extends string = string> extends IVssService, IObservable<T, TAction> { }

/**
 * An ObservableService is a service that allows callers to signup for notifications when
 * data within the service is changed.
 */
export abstract class VssObservableService<T, TAction extends string = string> extends VssService implements IVssObservableService<T, TAction> {
    private observable = new Observable<T>();

    public subscribe(observer: (value: T, action: TAction) => void, action?: string): (value: T, action: TAction) => void {
        return this.observable.subscribe(observer, action);
    }

    public unsubscribe(observer: (value: T, action: TAction) => void, action?: string): void {
        this.observable.unsubscribe(observer, action);
    }

    protected _notify(value: T, action: TAction, persistEvent?: boolean) {
        this.observable.notify(value, action, persistEvent);
    }
}

/**
 * A service factory is the method used to create and initialize an IVssService.
 */
export type ServiceFactory = new () => IVssService;

/**
 * ServiceOptions are optional values that can be used to describe services
 * registered with the core service provider.
 */
export const enum ServiceOptions {
    /**
     * Services registered with the Remotable flag may be retrieved and called across
     * the XDM message channel. Ensure no methods are exposed unintentionally on
     * remotable services.
     */
    Remotable = 0x1,

    /**
     * Persistant services are not restarted when a new page context is created. They
     * are only ever created once. This is used to maintain state across FPS navigations.
     * This should generally NOT be used. An example where it is used it the content
     * service that tracks the set of scripts that have been loaded.
     */
    Persistant = 0x2,

    /**
     * Services can request to be always available for a given PageContext. This means
     * that they will be created as soon as they are registered for any PageContext
     * instances that already exist, and new ones will be created as they are
     * registered.
     */
    Startup = 0x4,

    /**
     * Private services are usually startup services that perform some background task
     * but are not accessible through the GetService API. A unique name is still required
     * for private services.
     */
    Private = 0x8
}

/**
 * Details that describe how a service is created and its capabilities.
 */
export interface IServiceDefinition {
    /**
     * This is the constructor used to create an instance of the service.
     */
    serviceFactory: ServiceFactory;

    /**
     * Services may support specific capabilities that the platform tracks.
     *
     * An example is whether or not a service is "remotable" or not. Remotable services
     * can be used across the frame boundary while non-remotable may only be used within.
     * the frame they are defined.
     */
    options?: ServiceOptions;
}

/**
 * Create and expose the service registry to the platform for developers to register
 * their services.
 */
export const Services: IObservableObject<IServiceDefinition> = new ObservableObject<IServiceDefinition>();

/**
 * Serialization settings when sending an object across iframe boundaries
 */
export interface IRemoteSerializationSettings {
    /**
     * If true, functions on this object of type "function" will have proxy functions inserted on the
     * object in the remote iframe which will invoke the method in this frame.
     *
     * For security reasons, this should ONLY be set to true if you trust arbitrary third-party extensions
     * to call any method on the object
     */
    proxyFunctions?: boolean;

    /**
     * If true, serialize properties that start with an underscore. By default properties that start with
     * an underscore are not serialized across iframe boundaries.
     */
    serializeUnderscoreProperties?: boolean;

    /**
     * If specified, the property names that map to true in the provided dictionary will not be
     * serialized across the iframe boundary
     */
    ignoredProperties?: { [propertyName: string]: boolean };
}

/**
 * The key that will be examined on any object being sent across the iframe boundary. If present,
 * it will override the default serialization settings.
 */
export const RemoteSerializationSettingsKey = "__remoteSerializationSettings";

/**
 * Interface for a class that can retrieve authorization tokens to be used in fetch requests.
 */
export interface IAuthorizationTokenProvider {
    /**
     * Gets the authorization header to use in a fetch request
     *
     * @param forceRefresh If true, indicates that we should get a new token, if applicable for current provider.
     * @return the value to use for the Authorization header in a request.
     */
    getAuthorizationHeader(forceRefresh?: boolean): Promise<string>;

    /**
     * If specified, the name of this provider will be added as a X-VSS-ClientAuthProvider header to the fetch request
     */
    providerName?: string;
}

/**
 * A rest client provides methods that issue remote REST calls.
 */
export interface IVssRestClient { }

/**
 * A REST client factory is the method used to create and initialize an IVssRestClient.
 */
export type RestClientFactory = new (options: IVssRestClientOptions) => IVssRestClient;

/**
 * Host level for a VSS service
 */
export const enum TeamFoundationHostType {
    /**
     * The Deployment host
     */
    Deployment = 1,

    /**
     * The Organization host
     */
    Organization = 2,

    /**
     * The Enterprise host
     */
    Enterprise = 2,

    /**
     * The project collection host
     */
    ProjectCollection = 4
}

/**
 * Options for registering a REST client.
 */
export interface IVssRestClientRegistrationOptions {
    /**
     * The id of the service instance type that the client should target (unless overridden by specific IVssRestClientOptions).
     */
    serviceInstanceType?: string;

    /**
     * The REST API resource area id used by this REST client
     */
    resourceAreaId?: string;
}

/**
 * Determines how a RestClient should respond to an error.
 *
 * Keep in sync with old web platform declaration in VSS/WebApi/RestClient
 */
export const enum RestErrorHandlerResponse {
    /**
     * Retry the request
     */
    Retry = 1,
    /**
     * Continue with the error handling process
     */
    Continue = 2
}

export type RestErrorHandler = (
    pageContext: IVssPageContext,
    tries: number,
    text: string | undefined,
    response: Response | undefined,
    error: Error
) => Promise<RestErrorHandlerResponse>;

/**
 * Options for a specific instance of a REST client.
 */
export interface IVssRestClientOptions {
    /**
     * The REST API resource area id used by this REST client
     */
    resourceAreaId?: string;

    /**
     * The id of the service instance type that the client should target. Ignored if an explicit rootPath is supplied.
     */
    serviceInstanceType?: string;

    /**
     * When supplying serviceInstanceType, this indicates the host level of the service to target.
     */
    hostType?: TeamFoundationHostType;

    /**
     * Auth token manager that can be used to get and attach auth tokens to requests.
     * If not supplied, the default token provider is used if the serviceInstanceType option is supplied
     * and is different from the hosting page's service instance type.
     */
    authTokenProvider?: IAuthorizationTokenProvider;

    /**
     * The root URL path to use for this client. Can be relative or absolute.
     */
    rootPath?: string | Promise<string>;

    /**
     * Current session id.
     */
    sessionId?: string;

    /**
     * Current command for activity logging.
     */
    command?: string;

    /**
     * The current page context. If not provided, this should be set automatically.
     */
    pageContext?: IVssPageContext;

    /**
     * Handles errors in sending the request or parsing the response. Intended for platform use only.
     */
    errorHandler?: RestErrorHandler;

    /**
     * If set to true, this request will be sent immediately even if the platform is attempting to resolve REST errors.
     * Intended for platform use only.
     */
    ignoreQueue?: boolean;

    /**
     * (Optional) Read consistency level (With relaxed setting like 'Eventual' the server storage traffic could redirect to eg SQL read-only replica) 
     */
    readConsistencyLevel?: IVssReadConsistencyLevel;
}

export enum IVssReadConsistencyLevel {
    /**
     * Strict is default ensures we read from SQL primary replica or strict mode in cosmos DB, ensure read latest committed data
     */
    Strict = 0,

    /**
     * Eventual allows read from SQL read-only replica or more relaxed mode in cosmos DB, may have delay and does not guarantee order retrieving committed changes (eg in the multi read-replica scenario) 
     */
    Eventual = 1
}

/**
 * Service for resolving registered REST client options for the current page context
 */
export interface IRestClientOptionsProviderService extends IVssService {
    /**
     * Fills in REST client options relevant to the current page context such as the
     * rootUrl and session Id.
     *
     * @param registrationOptions Options that the REST client factory was registered with
     * @param instanceOptions Options to use for this specific REST client instance
     */
    populateRestClientOptions(
        registrationOptions: IVssRestClientRegistrationOptions | undefined,
        instanceOptions: IVssRestClientOptions | undefined
    ): IVssRestClientOptions;
}

/**
 * Details that describe how a REST client is created and its capabilities.
 */
export interface IRestClientDefinition {
    /**
     * This is the constructor used to create an instance of the service.
     */
    factory: RestClientFactory;

    /**
     * The default options for creating clients of this type
     */
    options?: IVssRestClientRegistrationOptions;
}

/**
 * Create and expose the service registry to the platform for developers to register
 * their services.
 */
export const RestClients: IObservableObject<IRestClientDefinition> = new ObservableObject<IRestClientDefinition>();

/**
 * The IVssPageContext is at the core of the VSS platform. It acts as the
 * service provider for the page. Callers should register and interact with
 * API's through the pageContext instead of dealing directly with instances.
 * This supports a better seperation of concerns and encapsulation within
 * service objects through interfaces.
 */
export interface IVssPageContext {
    /**
     * Get a service by service name. The caller supplies the 'expected' type and
     * this method returns it as this type. There is no actual validation so it is
     * the responsibility of the caller to ensure the type is correct.
     *
     * @param serviceName - name of the service to retrieve.
     */
    getService: <T extends IVssService>(serviceName: string) => T;

    /**
     * Get a REST client by its registered name. The caller supplies the
     * 'expected' type and this method returns it as this type. There is no actual
     * validation so it is the responsibility of the caller to ensure the type is correct.
     */
    getRestClient: <T extends IVssRestClient>(restClientName: string, options?: IVssRestClientOptions) => T;

    /**
     * Observable value tracking whether or not startup services have been started yet.
     * This happens during DOM Content loaded for the initial page load. On FPS events this
     * is reset when an FPS starts, and set to true once services have been (re-)started.
     */
    platformInitialized: IObservableValue<boolean>;

    /**
     * For callers that want a unique number for this page. They can call getUniqueNumber().
     */
    getUniqueNumber(): number;
}

export interface IVssPageContextInternal {
    /**
     * Get a service by service name. The caller supplies the 'expected' type and
     * this method returns it as this type. There is no actual validation so it is
     * the responsibility of the caller to ensure the type is correct.
     *
     * @param serviceName - name of the service to retrieve.
     * @param remoteOnly - If true, only return services marked as Remotable.
     * @param noThrowIfUndefined - If true, return undefined if the service is not defined rather than throwing.
     */
    getService<T extends IVssService>(serviceName: string, remoteOnly?: boolean, noThrowIfUndefined?: boolean): T | undefined;
}

/**
 * Core WebContext used by the web platform to manage the page being executed
 */
class VssPageContext implements IVssPageContext {
    // State information used to track services being initialized and destroyed.
    private initializationInProgress: { [serviceName: string]: boolean } = {};
    private contextUnloadInProgress: boolean;
    private contentLoaded: boolean;
    private delayedServices: string[] = [];

    // Details about the registered and active services.
    private serviceInstances: { [serviceName: string]: IVssService } = {};
    private services: IObservableObject<IServiceDefinition>;

    // Details about the registered rest clients
    private restClients: IObservableObject<IRestClientDefinition>;

    // Unique counter for the pageContext.
    private uniqueNumber: number = 0;

    public platformInitialized = new ObservableValue(false);

    constructor(serviceRegistry: IObservableObject<IServiceDefinition>, restClientRegistry: IObservableObject<IRestClientDefinition>) {
        this.services = serviceRegistry;
        this.restClients = restClientRegistry;

        // Start any new services that are registered after context creation that are startup
        const subscriber = (entry: IObjectProperty<IServiceDefinition>, action: string) => {
            if (action === "add") {
                if (entry && entry.value && entry.value.options && (entry.value.options & ServiceOptions.Startup) === ServiceOptions.Startup) {
                    if (this.contentLoaded) {
                        this.getService<IVssService>(entry.key);
                    } else {
                        this.delayedServices.push(entry.key);
                    }
                }
            }
        };

        // Subscribe to service changes to ensure we start any persistant ones upon registration
        this.services.subscribe(subscriber);

        // Wait until the document is loaded before starting up services
        const useAadToken = window.__useAadToken;
        const contentLoaded = () => {
            this.contentLoaded = true;

            // Start any services that were registered before DOM Content loaded
            for (const delayedServiceName of this.delayedServices) {
                this.getService<IVssService>(delayedServiceName);
            }

            // Startup services are now running
            this.platformInitialized.value = true;

            // Signup for FPS loading and completed events to reset the startupServicesReady
            document.body.addEventListener("fpsLoading", (event: FastPageSwitchEvent<FastPageSwitchEventLoadingDetail>) => {
                this.platformInitialized.value = false;
            });
            document.body.addEventListener("fpsCompleted", (event: FastPageSwitchEvent<FastPageSwitchEventCompletedDetail>) => {
                if (event.detail.success) {
                    this.platformInitialized.value = true;
                }
            });

            // Reset the service instances associated with the current page on fpsLoaded (occurs between loading and completed)
            document.body.addEventListener("fpsLoaded", (event: FastPageSwitchEvent<FastPageSwitchEventLoadedDetail>) => {
                const persistantInstances: { [serviceName: string]: IVssService } = {};

                // Mark the context as being unloaded while we shutdown existing services.
                this.contextUnloadInProgress = true;

                // Go through all active services at this point in time.
                for (const serviceName in this.serviceInstances) {
                    const serviceDefinition = this.services.get(serviceName);

                    // Save any persistant services for restart, and clear any non persistant services.
                    if (
                        serviceDefinition &&
                        serviceDefinition.options &&
                        (serviceDefinition.options & ServiceOptions.Persistant) === ServiceOptions.Persistant
                    ) {
                        persistantInstances[serviceName] = this.serviceInstances[serviceName];
                    } else {
                        // If the service implemented serviceEnd we will call it before removing from the context.
                        const registeredService = this.serviceInstances[serviceName];
                        if (registeredService._serviceEnd) {
                            try {
                                registeredService._serviceEnd(this);
                            } catch (exception) {
                                console.error(exception);
                            }
                        }

                        delete this.serviceInstances[serviceName];
                    }
                }

                this.contextUnloadInProgress = false;

                // First restart any services that already exist. This may get dependent services that are
                // marked as startup created.
                for (const serviceName in persistantInstances) {
                    const serviceInstance = persistantInstances[serviceName] as IVssPersistantService;

                    if (serviceInstance && serviceInstance._serviceRestart) {
                        serviceInstance._serviceRestart(this);
                    }
                }

                // Now create any services that are marked as startup services.
                for (const serviceName of this.services.keys()) {
                    const serviceDefinition = this.services.get(serviceName);

                    if (
                        serviceDefinition &&
                        serviceDefinition.options &&
                        (serviceDefinition.options & ServiceOptions.Startup) === ServiceOptions.Startup
                    ) {
                        if (!this.serviceInstances[serviceName]) {
                            this._loadService<IVssService>(serviceName, serviceDefinition);
                        }
                    }
                }
            });

            if (useAadToken) {
                document.dispatchEvent(new CustomEvent("NWPContentLoaded", {}));
            }
        }

        if (useAadToken) {
            document.addEventListener("authenticationInitialized", () => {
                contentLoaded();
            });

            // We need to ensure that MSAL.js has an opportunity to handle redirects
            // Otherwise, if a service like IVssNavigationService or IVssHistoryService updates the URL, we'll get into a loop
            document.addEventListener("DOMContentLoaded", () => {
                try {
                    this.getService("ITokenProviderService");
                }
                catch (error) {
                    console.log(error);
                    contentLoaded();
                }
            });
        }
        else {
            document.addEventListener("DOMContentLoaded", () => {
                contentLoaded();
            });
        }
    }

    public getService<T extends IVssService>(serviceName: string): T;
    public getService<T extends IVssService>(serviceName: string, remoteOnly?: boolean, noThrowIfUndefined?: boolean): T | undefined {
        if (!this.contextUnloadInProgress) {
            let registeredService = this.serviceInstances[serviceName];

            // If no service is available, we will start an instance of the service.
            if (!registeredService) {
                const serviceDefinition = this.services.get(serviceName);
                if (serviceDefinition) {
                    if (!serviceDefinition.options || (serviceDefinition.options & ServiceOptions.Private) !== ServiceOptions.Private) {
                        // Check service options if we are only fetching remotable services
                        if (
                            remoteOnly &&
                            serviceDefinition.options &&
                            (serviceDefinition.options & ServiceOptions.Remotable) !== ServiceOptions.Remotable
                        ) {
                            if (!noThrowIfUndefined) {
                                throw new Error(`The ${serviceName} service is not marked as remotable.`);
                            }
                        } else {
                            registeredService = this._loadService(serviceName, serviceDefinition);
                        }
                    }
                }

                if (!registeredService && !noThrowIfUndefined) {
                    throw new Error(`No service has been registered with the name "${serviceName}".`);
                }
            }

            return registeredService as T;
        } else {
            throw new Error("Services can't be requested while a shutdown is in progress");
        }
    }

    public getRestClient<T extends IVssRestClient>(restClientName: string, options?: IVssRestClientOptions): T {
        // Lookup a definition based on client name.
        const clientDefinition = this.restClients.get(restClientName);
        if (!clientDefinition) {
            throw new Error(`No REST client has been registered with the name "${restClientName}".`);
        }

        // Merge options into the default options and create a new client
        const optionsProviderService = this.getService<IRestClientOptionsProviderService>("IRestClientOptionsProviderService");
        const resolvedOptions = optionsProviderService.populateRestClientOptions(clientDefinition.options, options);

        const clientInstance = new clientDefinition.factory(resolvedOptions);
        return clientInstance as T;
    }

    public getUniqueNumber(): number {
        return ++this.uniqueNumber;
    }

    private _loadService<T>(serviceName: string, serviceDefinition: IServiceDefinition): T {
        let registeredService: IVssService;

        if (this.initializationInProgress[serviceName]) {
            throw new Error("Unable to initialize service due to cyclic dependency: " + serviceName);
        }

        try {
            // Mark this service as initialization in progress to detect cyclic dependencies in _serviceStart.
            this.initializationInProgress[serviceName] = true;

            // Create and initialize a new instance of the service.
            registeredService = new serviceDefinition.serviceFactory();
            if (serviceDefinition.options && (serviceDefinition.options & ServiceOptions.Remotable) === ServiceOptions.Remotable) {
                setRemoteSerializationSettings(registeredService, {
                    proxyFunctions: true,
                    ignoredProperties: { pageContext: true }
                });
            }
            if (registeredService._serviceStart) {
                registeredService._serviceStart(this);
            }
        } catch (exception) {
            delete this.initializationInProgress[serviceName];
            throw exception;
        }

        // Save this service in the registered services.
        this.serviceInstances[serviceName] = registeredService;
        delete this.initializationInProgress[serviceName];

        return registeredService as T;
    }
}

/**
 * Sets the remote serialization settings used when the specified object is serialized
 * across iframe boundaries
 *
 * @param obj The object to set serialization settings on
 * @param settings Settings used when serializing the object across iframe boundaries
 */
export function setRemoteSerializationSettings(obj: any, settings: IRemoteSerializationSettings) {
    obj[RemoteSerializationSettingsKey] = settings;
}

/**
 * The FastPageSwitchEvent is the type of event that is dispatched for each of the
 * navigation events. Based on the event name the detail will be of Type T and they are
 * described below.
 */
export interface FastPageSwitchEvent<T> extends CustomEvent {
    detail: T;
}

/**
 * Contracts used for FPS Navigation, these are defined here since it is used by the
 * context and other services.
 */
export interface FastPageSwitchEventDetail {
    /**
     * If this event is cancelled, this property contains the cancellation reason.
     */
    cancellationReason?: string;

    /**
     * Optional data past to the FastPageSwitch request.
     */
    data?: { [key: string]: any };

    /**
     * The URL the FastPageSwitch is using for the source of the data.
     */
    href: string;

    /**
     * The pageContext for the page before it is navigated.
     */
    pageContext: IVssPageContext;

    /**
     * Defines whether or not a new history entry should be added when this navigation occurs.
     */
    recordHistoryEntry: boolean;

    /**
     * The time at which the fast-page-switch was requested/started.
     */
    startTime: number;
}

/**
 * The FPSStarting event is dispatched before a FastPageSwitch starts. This
 * gives all interested parties the ability to react to the event. The event occurs
 * on the document.body, doesn't bubble, and IS cancelable.
 *
 * If any event listener prevents the default behavior the navigation is canceled
 * and no further actions are taken.
 */
export interface FastPageSwitchEventStartingDetail extends FastPageSwitchEventDetail { }

/**
 * The FPSLoad event is dispatched after all FPSStarting event listeners have had
 * a chance to process the event. This event is dispatched before the navigation
 * call to the server and should be used to clean up any state. Callers shouldn't
 * do any extended processing here. This should be scheduled.
 */
export interface FastPageSwitchEventLoadingDetail extends FastPageSwitchEventDetail { }

/**
 * The FPSLoaded event is dispatched after the request has completed successfully.
 * Success is defined as a 200 HttpStatusCode result, with the proper data being
 * returned.
 */
export interface FastPageSwitchEventLoadedDetail extends FastPageSwitchEventDetail { }

/**
 * The FPSCompleted event is dispatched after all event listeners have finished
 * processing the FPSLoaded event. At this point the platform has completed its
 * updates and all services will now be setup with the new page context.
 *
 * The FPSCompleted event will fire even if the navigation fails. But the success
 * property will be set to false and a reason will be given.
 */
export interface FastPageSwitchEventCompletedDetail extends FastPageSwitchEventDetail {
    /**
     * Whether or not the FastPageSwitch completed successfully.
     */
    success: boolean;

    /**
     * The reason is an object used to describe the reason for the navigation failure.
     */
    reason?: any;
}

/**
 * Context for the currently executing page
 */
const PageContext: IVssPageContext = new VssPageContext(Services, RestClients);

/**
 * Don't allow pageContext recursive properties to be serialized across iframe boundaries - will guaranteed result in an infinite serialization loop
 */
setRemoteSerializationSettings(PageContext, {
    proxyFunctions: false,
    ignoredProperties: { pageContext: true, serviceInstances: true, services: true, restClients: true }
});

export function getDefaultRawPageContext(contributionId: string): PageContext | undefined {
    if (!defaultRawPageContext) {
        const contributionService = (PageContext as IVssPageContextInternal).getService("IVssContributionService", undefined, true) as any;
        const contributionDate = contributionService?.getData(contributionId) as any;
        defaultRawPageContext = contributionDate?.pageContext;
    }

    return defaultRawPageContext;
}

export const getPageContext = (contributionId: string): PageContext | undefined => {
    if (!defaultPageContext) {
        const rawContext = getDefaultRawPageContext(contributionId);
        if (rawContext) {
            defaultPageContext = deserializeVssJsonObject<PageContext>(JSON.stringify(rawContext)) ?? undefined;
        }
    }

    return defaultPageContext;
};

export function getService<T extends IVssService>(serviceName: string): T | undefined {
    return (PageContext as IVssPageContextInternal).getService<T>(serviceName, undefined, true);
}
